import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone, Share2, Plus, MoreVertical, ChevronDown } from 'lucide-react';
import usePWAStandalone, { detectPlatform } from '@/hooks/usePWAStandalone';

/**
 * Botón universal para instalar la PWA de AIGIS Auditor.
 * - Si el navegador soporta `beforeinstallprompt`, lo dispara con un click.
 * - Si no lo soporta (iOS Safari o Chrome sin heurística), abre un modal
 *   con instrucciones específicas de la plataforma detectada.
 * - Se oculta si la app ya está instalada (modo standalone).
 */
export default function InstallPWAButton({ className = '', style = {}, variant = 'primary' }) {
    const [prompt, setPrompt] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const isStandalone = usePWAStandalone();
    const platform = detectPlatform();

    useEffect(() => {
        const onBIP = (e) => { e.preventDefault(); setPrompt(e); };
        const onInstalled = () => setPrompt(null);
        window.addEventListener('beforeinstallprompt', onBIP);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBIP);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    if (isStandalone) return null;

    const handleClick = async () => {
        if (prompt) {
            try {
                prompt.prompt();
                await prompt.userChoice;
                setPrompt(null);
                return;
            } catch (e) { /* fallthrough al modal */ }
        }
        setShowModal(true);
    };

    const baseCls = variant === 'primary'
        ? 'text-white'
        : 'border-2 bg-transparent';
    const primaryStyle = variant === 'primary' ? { background: '#00875A' } : { borderColor: '#00875A', color: '#00875A' };

    return (
        <>
            <button onClick={handleClick} data-testid="pwa-install-btn"
                title="Instalar como app en tu tablet o móvil"
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold hover:opacity-90 ${baseCls} ${className}`}
                style={{ ...primaryStyle, ...style }}>
                <Download className="w-3.5 h-3.5" />
                Instalar app
            </button>

            {showModal && (
                <InstallInstructionsModal platform={platform} onClose={() => setShowModal(false)} />
            )}
        </>
    );
}

function InstallInstructionsModal({ platform, onClose }) {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.65)' }} onClick={onClose}
            data-testid="pwa-install-instructions-modal">
            <div className="max-w-md w-full max-h-[90vh] overflow-y-auto border"
                style={{ background: '#1A1A1A', borderColor: 'rgba(255,255,255,0.10)', color: 'white' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.10)' }}>
                    <div className="flex items-center gap-2">
                        <Smartphone className="w-5 h-5" style={{ color: '#00875A' }} />
                        <div className="font-display font-black">Instalar AIGIS Auditor</div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/10"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4 text-sm text-white">
                    <p className="opacity-80">
                        Para instalar la app en tu dispositivo y usarla como una aplicación nativa (con ícono en la pantalla de inicio), sigue estos pasos:
                    </p>

                    {platform === 'ios' && <IOSSteps />}
                    {platform === 'android' && <AndroidSteps />}
                    {platform === 'desktop' && <DesktopSteps />}

                    <div className="p-3 border-l-4 text-xs" style={{ background: 'rgba(0,135,90,0.10)', borderColor: '#00875A' }}>
                        💡 Una vez instalada, la app se abrirá directamente en el modo Auditor, sin barras del navegador ni acceso al resto de la plataforma.
                    </div>
                </div>
            </div>
        </div>
    );
}

const StepRow = ({ n, Icon, text }) => (
    <div className="flex items-start gap-3">
        <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ background: '#00875A' }}>{n}</div>
        <div className="flex-1 pt-1">
            <div className="text-sm flex items-center gap-1.5 flex-wrap">{text}
                {Icon && <Icon className="w-4 h-4 inline opacity-70" />}
            </div>
        </div>
    </div>
);

function IOSSteps() {
    return (
        <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">iPad / iPhone (Safari)</div>
            <StepRow n={1} Icon={Share2} text={<span>Toca el botón <b>Compartir</b> en la barra inferior de Safari</span>} />
            <StepRow n={2} Icon={Plus} text={<span>Elige <b>&quot;Añadir a pantalla de inicio&quot;</b></span>} />
            <StepRow n={3} text={<span>Confirma el nombre <b>AIGIS Auditor</b> y toca <b>&quot;Añadir&quot;</b></span>} />
            <StepRow n={4} text={<span>Listo — busca el ícono verde de AIGIS en tu pantalla de inicio</span>} />
        </div>
    );
}
function AndroidSteps() {
    return (
        <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Android (Chrome)</div>
            <StepRow n={1} Icon={MoreVertical} text={<span>Toca el menú <b>⋮</b> (arriba a la derecha de Chrome)</span>} />
            <StepRow n={2} text={<span>Elige <b>&quot;Instalar app&quot;</b> o <b>&quot;Añadir a pantalla principal&quot;</b></span>} />
            <StepRow n={3} text={<span>Confirma tocando <b>&quot;Instalar&quot;</b></span>} />
            <StepRow n={4} text={<span>La app aparecerá en tu cajón de aplicaciones como cualquier app nativa</span>} />
            <div className="text-xs opacity-70 mt-2">
                <b>Nota:</b> Si no ves &quot;Instalar app&quot;, cierra Chrome, vuelve a abrir la URL y espera unos segundos. Chrome requiere haber visitado el sitio al menos una vez.
            </div>
        </div>
    );
}
function DesktopSteps() {
    return (
        <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Escritorio (Chrome / Edge)</div>
            <StepRow n={1} text={<span>Busca el ícono <b>⊕</b> en la barra de dirección (a la derecha)</span>} />
            <StepRow n={2} text={<span>Haz click y confirma <b>&quot;Instalar&quot;</b></span>} />
            <StepRow n={3} text={<span>Se abrirá una ventana propia sin barras del navegador</span>} />
            <div className="text-xs opacity-70">
                Alternativamente: menú <ChevronDown className="w-3 h-3 inline" /> del navegador → <b>Instalar AIGIS Auditor...</b>
            </div>
        </div>
    );
}
